import { DatePicker } from '@/components/ui/date-picker';
import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import CurrencyInput from '../components/CurrencyInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatDate, getTodayDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Users2,
  Filter,
  Phone,
  Mail,
  Sparkles,
  Shield,
  Wrench,
  UserCog,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const positionConfig = {
  kebersihan: { label: 'Kebersihan', icon: Sparkles, color: 'text-green-600' },
  keamanan: { label: 'Keamanan', icon: Shield, color: 'text-purple-600' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'text-orange-600' },
  admin: { label: 'Admin', icon: UserCog, color: 'text-blue-600' },
  lainnya: { label: 'Lainnya', icon: Users2, color: 'text-gray-600' },
};

const statusConfig = {
  aktif: { label: 'Aktif', color: 'bg-emerald-100 text-emerald-700' },
  nonaktif: { label: 'Non-Aktif', color: 'bg-gray-100 text-gray-700' },
};

const StaffForm = ({ staff, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    position: staff?.position || 'kebersihan',
    phone: staff?.phone || '',
    email: staff?.email || '',
    address: staff?.address || '',
    salary: staff?.salary || '',
    start_date: staff?.start_date || getTodayDate(),
    schedule: staff?.schedule || '',
    status: staff?.status || 'aktif'
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Mohon isi nama dan nomor telepon');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        salary: formData.salary ? parseFloat(formData.salary) : 0
      };

      if (staff?.id) {
        await axios.put(`${API_URL}/api/staff/${staff.id}`, payload);
        toast.success('Data staff berhasil diperbarui');
      } else {
        await axios.post(`${API_URL}/api/staff`, payload);
        toast.success('Staff berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving staff:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data staff');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nama Lengkap *</Label>
          <Input
            id="name"
            name="name"
            placeholder="Nama staff"
            value={formData.name}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="staff-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="position">Posisi *</Label>
          <Select value={formData.position} onValueChange={(value) => setFormData(prev => ({ ...prev, position: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="staff-position-select">
              <SelectValue placeholder="Pilih posisi" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(positionConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">No. Telepon *</Label>
          <Input
            id="phone"
            name="phone"
            placeholder="08xxxxxxxxxx"
            value={formData.phone}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="staff-phone-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="email@contoh.com"
            value={formData.email}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">Alamat</Label>
        <Input
          id="address"
          name="address"
          placeholder="Alamat lengkap"
          value={formData.address}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="salary">Gaji</Label>
          <CurrencyInput
            id="salary"
            name="salary"
            placeholder="0"
            value={formData.salary}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="staff-salary-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="start_date">Tanggal Mulai</Label>
          <DatePicker
            id="start_date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="schedule">Jadwal Kerja</Label>
        <Input
          id="schedule"
          name="schedule"
          placeholder="Contoh: Senin-Jumat 08:00-17:00"
          value={formData.schedule}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
        />
      </div>

      {staff?.id && (
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl">
              <SelectValue placeholder="Pilih status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="nonaktif">Non-Aktif</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="staff-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : staff?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPosition, setFilterPosition] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const fetchStaff = async () => {
    try {
      const params = new URLSearchParams();
      if (filterPosition) params.append('position', filterPosition);
      if (filterStatus) params.append('status', filterStatus);
      const response = await axios.get(`${API_URL}/api/staff?${params.toString()}`);
      setStaffList(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Gagal memuat data staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPosition, filterStatus]);

  const handleEdit = (staff) => {
    setSelectedStaff(staff);
    setDialogOpen(true);
  };

  const handleDelete = (staff) => {
    setSelectedStaff(staff);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/staff/${selectedStaff.id}`);
      toast.success('Staff berhasil dihapus');
      fetchStaff();
    } catch (error) {
      toast.error('Gagal menghapus staff');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedStaff(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedStaff(null);
    fetchStaff();
  };

  const activeStaff = staffList.filter(s => s.status === 'aktif');
  const totalSalary = activeStaff.reduce((sum, s) => sum + (s.salary || 0), 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Manajemen Staff</h1>
          <p className="text-muted-foreground mt-1">Kelola data karyawan kost</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-staff-btn">
          <Plus className="w-5 h-5 mr-2" />
          Tambah Staff
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users2 className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Total Staff</span>
          </div>
          <p className="text-2xl font-bold">{staffList.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users2 className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-muted-foreground">Staff Aktif</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{activeStaff.length}</p>
        </Card>
        <Card className="p-4 col-span-2">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-accent" />
            <span className="text-xs text-muted-foreground">Total Gaji / Bulan</span>
          </div>
          <p className="text-2xl font-bold text-accent">{formatCurrency(totalSalary)}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter:</span>
        </div>
        <Select value={filterPosition || "all"} onValueChange={(v) => setFilterPosition(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Posisi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Posisi</SelectItem>
            {Object.entries(positionConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="aktif">Aktif</SelectItem>
            <SelectItem value="nonaktif">Non-Aktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </Card>
      ) : staffList.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Users2 className="empty-state-icon" />
            <p className="empty-state-title">Belum ada staff</p>
            <p className="empty-state-description">Tambahkan data karyawan kost</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Posisi</TableHead>
                <TableHead>Kontak</TableHead>
                <TableHead>Jadwal</TableHead>
                <TableHead>Gaji</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staffList.map((staff) => {
                const posConfig = positionConfig[staff.position] || positionConfig.lainnya;
                const statConfig = statusConfig[staff.status] || statusConfig.aktif;
                const Icon = posConfig.icon;
                return (
                  <TableRow key={staff.id} data-testid={`staff-row-${staff.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <Icon className={`w-5 h-5 ${posConfig.color}`} />
                        </div>
                        <div>
                          <p className="font-medium">{staff.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Mulai: {formatDate(staff.start_date)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{posConfig.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          {staff.phone}
                        </div>
                        {staff.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            {staff.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {staff.schedule || '-'}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(staff.salary || 0)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statConfig.color}>{statConfig.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(staff)}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(staff)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />Hapus
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedStaff ? 'Edit Staff' : 'Tambah Staff Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedStaff ? 'Perbarui data staff' : 'Tambahkan data karyawan baru'}
            </DialogDescription>
          </DialogHeader>
          <StaffForm
            staff={selectedStaff}
            onSave={handleSave}
            onClose={() => { setDialogOpen(false); setSelectedStaff(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Staff?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus &quot;{selectedStaff?.name}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Staff;
